import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { DsTooltipDirective } from 'src/app/shared/components/ds-tooltip/ds-tooltip.component';
import { Subject, combineLatest, filter, takeUntil } from 'rxjs';
import { TenantService } from 'src/app/modules/administration/tenant/tenant.service';
import { pathRequiresTenant } from 'src/app/core/auth/guards/tenant-exempt-paths';
import { moduleSubNavConfig, MODULE_TITLE_MAP, SubNavItem } from './module-sub-nav.config';
import { environment } from 'src/environments/environment';
import { version as packageVersion } from '../../../../../package.json';
import { UserResourceDirective } from 'src/app/shared/directives/has-resource/user-resource.directive';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'context-sidebar',
  imports: [RouterModule, TranslateModule, UserResourceDirective, DsTooltipDirective],
  templateUrl: './context-sidebar.component.html',
  styleUrls: ['./context-sidebar.component.scss'],
})
export class ContextSidebarComponent implements OnInit, OnDestroy {
  /** Whether the sidebar is visible (expanded). Controlled by parent or internal toggle. */
  @Input() collapsed = false;

  /** Emits when the collapsed state changes so the parent layout can react. */
  @Output() collapsedChange = new EventEmitter<boolean>();

  /** Currently active top-level module path segment (e.g. '/sales'). */
  activeModuleBase = '';

  /** i18n key for the active module title. */
  activeModuleTitle = '';

  /** Material symbol icon for the active module. */
  activeModuleIcon = '';

  /** Sub-navigation items for the active module. */
  currentSubNav: SubNavItem[] = [];

  /**
   * The ONE sub-nav path that is highlighted, resolved by longest prefix match.
   *
   * `routerLinkActive` (which this replaces) has only two modes and neither is
   * right here. Non-exact lights up every ancestor: `/logistics/freight-forwarding`
   * ("Vận chuyển hàng hóa") stayed highlighted alongside Shipment, Master Bill and
   * House Bill, because their routes are nested UNDER it — two active rows at once.
   * Exact would fix that but kill the highlight on every detail page, since
   * `/logistics/freight-forwarding/house-bill/:id` is not literally the nav path.
   *
   * Longest match wins gives both: a detail page still lights its list parent, and
   * a hub yields to any child that matches more of the URL. Seven items across six
   * modules sat under a shadowing parent when this was measured (24/08/2026).
   */
  activeNavPath = '';

  /** Whether the app is running in production mode. */
  isProduction = environment.production;

  /** App version — imported directly from package.json so CI's environment.ts override doesn't need to carry it. */
  appVersion = packageVersion;

  /** Config icons are legacy svg-registry keys ('mat_outline:speed') — strip the
   * namespace so the Material Symbols font span can render the glyph directly
   * (Material-free shell, DS-CAROT-ALIGN-01). */
  iconName(icon: string): string {
    const i = icon.indexOf(':');
    return i === -1 ? icon : icon.slice(i + 1);
  }

  /**
   * Dòng phụ dưới tên module = tên tenant đang chọn (sales-shell.jsx: "Sales · Calía").
   * Rỗng khi chưa chọn tenant — dòng phụ ẩn, không in chữ giữ chỗ.
   */
  tenantName = '';

  /**
   * Tài khoản đã có ≥1 tenant chưa. Chưa có ⇒ mục thuộc module nghiệp vụ mờ .42 (thiết kế
   * "tenant chưa chọn → mục mờ"). Cùng nguồn `tenants$` mà tenantGuard và module rail đọc,
   * nên ba nơi không bao giờ bất đồng. Chỉ là trình bày: guard vẫn là chốt chặn thật.
   */
  hasTenant = true;

  private _tenantService = inject(TenantService);

  /**
   * Icon của một mục — mục không khai icon dùng chấm tròn để hàng vẫn thẳng cột (v2 vẽ icon cho
   * MỌI mục, kể cả khi thu gọn). Đặt ở hàm thay vì ternary trong template: bộ quét icon
   * (`build-icon-subset.mjs` ICON_FUNCTION) đọc nhầm khối điều khiển sau `iconName(...) : '…'`.
   */
  navIcon(item: SubNavItem): string {
    return item.icon ? this.iconName(item.icon) : 'circle';
  }

  private _unsubscribeAll = new Subject<boolean>();

  constructor(
    private _router: Router,
    private _cdr: ChangeDetectorRef,
  ) {}

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    // Derive module from initial URL
    this._updateActiveModule(this._router.url);

    // React to subsequent navigations
    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe((event) => {
        this._updateActiveModule(event.urlAfterRedirects || event.url);
      });

    combineLatest([this._tenantService.selectedTenant$, this._tenantService.tenants$])
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(([tenant, tenants]) => {
        this.tenantName = tenant?.displayName || tenant?.name || '';
        this.hasTenant = (tenants?.length ?? 0) > 0;
        this._cdr.markForCheck();
      });
  }

  /** Mục mờ vì tài khoản chưa có tenant mà route của nó cần tenant. */
  isLocked(item: SubNavItem): boolean {
    return !this.hasTenant && pathRequiresTenant(item.path);
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(true);
    this._unsubscribeAll.complete();
  }

  // ---------------------------------------------------------------------------
  // Public helpers
  // ---------------------------------------------------------------------------

  /** Toggle collapsed state */
  toggle(): void {
    this.collapsed = !this.collapsed;
    this.collapsedChange.emit(this.collapsed);
  }

  // isActive() removed — replaced by routerLinkActive directive in template for performance

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  /** Map of module paths to Material Symbol icon names for the sidebar header. */
  private static readonly MODULE_ICON_MAP: Record<string, string> = {
    '/pos': 'point_of_sale',
    '/sales': 'shopping_bag',
    '/warehouse': 'inventory_2',
    '/logistics': 'local_shipping',
    '/crm': 'groups',
    '/procurement': 'account_tree',
    '/planning': 'event_note',
    '/manufacturing': 'factory',
    '/project-management': 'work_outline',
    '/finance': 'account_balance',
    '/pricing': 'sell',
    '/hrm': 'badge',
    '/asset-management': 'business_center',
    '/okr-kpi': 'monitoring',
    '/master-data': 'database',
    '/fusion': 'hub',
    '/risk': 'query_stats',
    '/administration': 'admin_panel_settings',
    '/platform': 'terminal',
    '/governance': 'policy',
    '/profile': 'account_circle',
    '/knowledge': 'menu_book',
    '/component-library': 'palette',
  };

  /**
   * Parse the URL to extract the first-level module segment and look up
   * the corresponding sub-navigation config + title.
   */
  private _updateActiveModule(url: string): void {
    // Strip query params and fragments
    const cleanUrl = url.split('?')[0].split('#')[0];

    // Extract the first segment: '/sales/order/123' -> '/sales'
    const segments = cleanUrl.split('/').filter(Boolean);
    const base = segments.length > 0 ? `/${segments[0]}` : '';

    if (base !== this.activeModuleBase) {
      this.activeModuleBase = base;
      this.activeModuleTitle = MODULE_TITLE_MAP[base] || '';
      this.activeModuleIcon = ContextSidebarComponent.MODULE_ICON_MAP[base] || '';
      this.currentSubNav = moduleSubNavConfig[base] || [];
    }

    // Always recompute: navigating BETWEEN screens of the same module leaves
    // `base` unchanged, and that is exactly when the highlight has to move.
    this.activeNavPath = ContextSidebarComponent.resolveActiveNavPath(cleanUrl, this.currentSubNav);
    this._cdr.markForCheck();
  }

  /**
   * Longest sub-nav path that prefixes `url`. Exported as a static so the
   * contract spec can drive it with a URL list and no TestBed.
   */
  static resolveActiveNavPath(url: string, items: readonly SubNavItem[]): string {
    let best = '';
    for (const item of items) {
      const path = item.path.replace(/\/+$/, '');
      if (!path) continue;
      const matches = url === path || (!item.exact && url.startsWith(`${path}/`));
      if (matches && path.length > best.length) {
        best = path;
      }
    }
    return best;
  }
}
